import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  ApprovalPolicies,
  ChannelPolicies,
  GenerationModes,
  formatNumber,
  formatUsd,
  messages,
  type ApprovalPolicy,
  type CampaignDetail,
  type ChannelPolicy,
  type GenerationMode,
  type ListSummary,
  type Preflight,
  type Product,
  type Template,
} from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input, Select } from '@/components/ui/input';
import { SkeletonRows } from '@/components/ui/skeleton';
import { ConfirmDialog } from '@/components/common/feedback';
import { PageHeader } from '@/components/common/page';
import { useGoBack } from '@/lib/navigation';
import { cn, toDateTimeLocalInput } from '@/lib/utils';

const STEPS = [
  messages.campaign.stepBasic,
  messages.campaign.stepTargets,
  messages.campaign.stepMessages,
  messages.campaign.stepSchedule,
];

const stepFromParam = (raw: string | null): number => {
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 && n < STEPS.length ? n : 0;
};

/** 保存済みの下書きをフォームの値に戻す（プレビューから戻ったとき・リロードしたとき） */
const formFromDraft = (draft: CampaignDetail) => ({
  name: draft.name,
  productId: draft.product?.id ?? '',
  listId: draft.list?.id ?? '',
  channelPolicy: draft.channelPolicy as ChannelPolicy,
  templateId: draft.templateId ?? '',
  generationMode: draft.generationMode as GenerationMode,
  approvalPolicy: draft.approvalPolicy as ApprovalPolicy,
  tone: draft.tone,
  scheduledAt: draft.scheduledAt ? toDateTimeLocalInput(draft.scheduledAt) : '',
  immediate: false,
  dailyLimit: draft.dailyLimit,
  totalLimit: draft.totalLimit != null ? String(draft.totalLimit) : '',
  domainIntervalHours: draft.domainIntervalHours,
  allowedHours: draft.allowedHours,
  followupEnabled: draft.followupPolicy.enabled,
  followupDays: draft.followupPolicy.daysAfter[0] ?? 7,
});

/** S41 キャンペーン作成（4 ステップのウィザード。各ステップで下書き保存される） */
export const CampaignNewPage = () => {
  const navigate = useNavigate();
  const goBack = useGoBack('/campaigns');
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  // 下書きの ID と手順は URL に持つ。プレビューから戻ったときやリロード後も続きから再開できる
  const restoreId = searchParams.get('campaignId');
  const [step, setStep] = useState(() => stepFromParam(searchParams.get('step')));
  const [campaignId, setCampaignId] = useState<string | null>(restoreId);
  const [restored, setRestored] = useState(!restoreId);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [form, setForm] = useState({
    name: '',
    productId: '',
    // 企業一覧でリストを作ってから来た場合は、その対象リストを選んだ状態で始める
    listId: searchParams.get('listId') ?? '',
    channelPolicy: 'form_first' as ChannelPolicy,
    templateId: '',
    generationMode: 'ai' as GenerationMode,
    approvalPolicy: 'all' as 'all' | 'sample' | 'none',
    tone: 'polite' as 'polite' | 'concise',
    scheduledAt: '',
    /** 送信可能時間帯と 1 日あたり上限を無視して今すぐ全件送る */
    immediate: false,
    dailyLimit: 100,
    totalLimit: '',
    domainIntervalHours: 24,
    allowedHours: { daysOfWeek: [1, 2, 3, 4, 5], startHour: 9, endHour: 18 },
    followupEnabled: false,
    followupDays: 7,
  });

  const { data: draft, isError: draftMissing } = useQuery({
    queryKey: qk.campaign(restoreId ?? ''),
    queryFn: () => api.get<SingleResponse<CampaignDetail>>(`/campaigns/${restoreId}`),
    enabled: !!restoreId && !restored,
  });

  useEffect(() => {
    if (restored || !draft) return;
    if (draft.data.status !== 'draft') {
      // 開始済みのキャンペーンは作り直せない。詳細で状況を見てもらう
      navigate(`/campaigns/${draft.data.id}`, { replace: true });
      return;
    }
    setForm(formFromDraft(draft.data));
    setRestored(true);
  }, [draft, restored, navigate]);

  useEffect(() => {
    if (restored || !draftMissing) return;
    toast.error('下書きが見つからないため、新しく作成します');
    setCampaignId(null);
    setStep(0);
    setRestored(true);
  }, [draftMissing, restored]);

  useEffect(() => {
    if (!restored) return;
    const next = new URLSearchParams(searchParams);
    if (campaignId) next.set('campaignId', campaignId);
    else next.delete('campaignId');
    if (step > 0) next.set('step', String(step));
    else next.delete('step');
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [campaignId, step, restored, searchParams, setSearchParams]);

  const { data: products } = useQuery({
    queryKey: qk.products(),
    queryFn: () => api.get<SingleResponse<Product[]>>('/products'),
  });
  const { data: lists } = useQuery({
    queryKey: qk.lists,
    queryFn: () => api.get<SingleResponse<ListSummary[]>>('/lists'),
  });
  const { data: templates } = useQuery({
    queryKey: qk.templates(),
    queryFn: () => api.get<SingleResponse<Template[]>>('/templates'),
  });

  const { data: preflight, refetch: refetchPreflight } = useQuery({
    queryKey: ['campaigns', campaignId, 'preflight'],
    queryFn: () =>
      api.post<SingleResponse<Preflight>>(`/campaigns/${campaignId}/preflight`, {}),
    enabled: !!campaignId && step >= 1,
  });

  const saveDraft = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name,
        productId: form.productId,
        listId: form.listId,
        channelPolicy: form.channelPolicy,
        templateId: form.templateId || null,
        generationMode: form.generationMode,
        approvalPolicy: form.approvalPolicy,
        tone: form.tone,
        dailyLimit: form.dailyLimit,
        totalLimit: form.totalLimit ? Number(form.totalLimit) : null,
        domainIntervalHours: form.domainIntervalHours,
        allowedHours: form.allowedHours,
        scheduledAt: form.scheduledAt ? new Date(form.scheduledAt).toISOString() : null,
        followupPolicy: {
          enabled: form.followupEnabled,
          daysAfter: [form.followupDays],
          stopOnReply: true,
          maxCount: 1,
        },
      };
      if (campaignId) return api.patch<SingleResponse<{ id: string }>>(`/campaigns/${campaignId}`, payload);
      return api.post<SingleResponse<{ id: string }>>('/campaigns', payload);
    },
    onSuccess: (result) => {
      setCampaignId(result.data.id);
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const generate = useMutation({
    mutationFn: () =>
      // チャネルは企業ごとに決まる（フォームが無ければメール）ため、ここでは指定しない
      api.post<SingleResponse<{ jobRunId: string; total: number }>>('/messages/bulk-generate', {
        campaignId,
        productId: form.productId,
        templateId: form.templateId || null,
        mode: form.generationMode,
        tone: form.tone,
      }),
    onSuccess: (result) => {
      toast.success(`${formatNumber(result.data.total)} 社分の文面の生成を開始しました`, {
        description: '完了すると通知でお知らせします',
      });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const start = useMutation({
    mutationFn: () =>
      api.post<SingleResponse<{ id: string; status: string }>>(`/campaigns/${campaignId}/start`, {
        confirmToken: preflight?.data.confirmToken,
        scheduledAt: form.immediate || !form.scheduledAt ? null : new Date(form.scheduledAt).toISOString(),
        immediate: form.immediate,
      }),
    onSuccess: (result) => {
      toast.success(result.data.status === 'scheduled' ? '予約しました' : '送信を開始しました');
      navigate(`/campaigns/${campaignId}`, { replace: true });
    },
    onError: (error) => {
      // 開始時に条件が変化していた場合は再確認を求める
      toast.error(errorMessage(error), { description: messages.campaign.preflightChanged });
      void refetchPreflight();
    },
  });

  /** 手順を切り替えたら先頭から見せる（同じ画面なのでスクロール制御は動かない） */
  const goToStep = (target: number) => {
    setStep(target);
    window.scrollTo(0, 0);
  };

  const next = async () => {
    if (step === 0) {
      if (!form.name || !form.productId || !form.listId) {
        toast.error('名称・商材・対象リストを入力してください');
        return;
      }
      await saveDraft.mutateAsync();
    } else if (step === 3) {
      await saveDraft.mutateAsync();
      setConfirmOpen(true);
      return;
    } else {
      await saveDraft.mutateAsync();
    }
    goToStep(Math.min(step + 1, STEPS.length - 1));
  };

  if (!restored) return <SkeletonRows rows={6} />;

  return (
    <>
      <PageHeader back="/campaigns" title={messages.campaign.create} />

      {/* ステップ表示 */}
      <ol className="mb-4 flex items-center gap-1 overflow-x-auto" aria-label="作成の手順">
        {STEPS.map((label, index) => (
          <li key={label} className="flex shrink-0 items-center gap-1">
            <span
              className={cn(
                'flex h-8 items-center gap-1.5 rounded-control px-3 text-note',
                index === step
                  ? 'bg-primary font-semibold text-primary-foreground'
                  : index < step
                    ? 'border border-border-strong text-foreground'
                    : 'border border-dashed border-border-strong text-foreground-subtle',
              )}
              aria-current={index === step ? 'step' : undefined}
            >
              {index < step ? <Check aria-hidden className="size-3.5" /> : `${index + 1}`}
              {label}
            </span>
            {index < STEPS.length - 1 && <span aria-hidden className="text-foreground-subtle">→</span>}
          </li>
        ))}
      </ol>

      {step === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{messages.campaign.stepBasic}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field label="名称" required hint="実績比較の単位になります">
              {(props) => (
                <Input
                  {...props}
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="9月_食材_東京"
                />
              )}
            </Field>
            <Field label="商材" required>
              {(props) => (
                <Select
                  {...props}
                  value={form.productId}
                  onChange={(e) => setForm((p) => ({ ...p, productId: e.target.value }))}
                >
                  <option value="">選択してください</option>
                  {products?.data.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="対象リスト" required hint={messages.list.dynamicNote}>
              {(props) => (
                <Select
                  {...props}
                  value={form.listId}
                  onChange={(e) => setForm((p) => ({ ...p, listId: e.target.value }))}
                >
                  <option value="">選択してください</option>
                  {lists?.data.map((list) => (
                    <option key={list.id} value={list.id}>
                      {list.name}（{formatNumber(list.companyCount)} 社・
                      {list.type === 'static' ? '静的' : '条件'}）
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={messages.campaign.channelPolicy} required>
              {(props) => (
                <Select
                  {...props}
                  value={form.channelPolicy}
                  onChange={(e) => setForm((p) => ({ ...p, channelPolicy: e.target.value as ChannelPolicy }))}
                >
                  {ChannelPolicies.list.map((policy) => (
                    <option key={policy.value} value={policy.value}>
                      {policy.label}（{policy.description}）
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </CardContent>
        </Card>
      )}

      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>{messages.campaign.stepTargets}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!preflight ? (
              <p className="text-body text-foreground-muted">{messages.common.loading}</p>
            ) : (
              <>
                <p className="tabular text-body">
                  対象: リスト「{lists?.data.find((l) => l.id === form.listId)?.name}」{' '}
                  {formatNumber(preflight.data.totalCandidates)} 社
                </p>
                <dl className="divide-y divide-border rounded-card border border-border">
                  <div className="flex items-center justify-between px-3 py-2.5">
                    <dt className="text-body font-semibold">{messages.campaign.sendable}</dt>
                    <dd className="tabular text-h3">{formatNumber(preflight.data.sendable)} 社</dd>
                  </div>
                  <div className="px-3 py-2.5">
                    <div className="flex items-center justify-between">
                      <dt className="text-body">{messages.campaign.blocked}</dt>
                      <dd className="tabular text-body">
                        {formatNumber(
                          preflight.data.blocked.reduce((sum, b) => sum + b.count, 0),
                        )}{' '}
                        社
                      </dd>
                    </div>
                    {/* 除外の内訳は必ず全件表示する */}
                    <ul className="mt-2 space-y-1">
                      {preflight.data.blocked.map((item) => (
                        <li key={item.reason} className="flex items-center justify-between gap-2 text-note">
                          <span className="text-foreground-muted">・{item.label}</span>
                          <span className="tabular">{formatNumber(item.count)} 社</span>
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() =>
                              navigate(
                                item.reason === 'no_contact'
                                  ? `/companies?listId=${form.listId}&hasEmail=false&hasForm=false`
                                  : item.reason === 'suppressed' || item.reason === 'excluded'
                                    ? `/companies?listId=${form.listId}&excluded=true`
                                    : `/companies?listId=${form.listId}`,
                              )
                            }
                          >
                            一覧
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex items-center justify-between px-3 py-2.5">
                    <dt className="text-body">チャネル内訳</dt>
                    <dd className="tabular text-body">
                      フォーム {preflight.data.channelBreakdown.form} / メール{' '}
                      {preflight.data.channelBreakdown.email}
                    </dd>
                  </div>
                </dl>
                {preflight.data.warnings.map((warning) => (
                  <p
                    key={warning}
                    className="flex items-start gap-2 rounded-control border-2 border-foreground bg-muted p-3 text-body font-semibold"
                  >
                    <AlertTriangle aria-hidden className="mt-0.5 size-5 shrink-0" />
                    {warning}
                  </p>
                ))}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>{messages.campaign.stepMessages}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field
              label={messages.campaign.generationMode}
              required
              hint={GenerationModes.meta(form.generationMode).description}
            >
              {(props) => (
                <Select
                  {...props}
                  value={form.generationMode}
                  onChange={(e) => setForm((p) => ({ ...p, generationMode: e.target.value as never }))}
                >
                  {GenerationModes.list.map((mode) => (
                    <option key={mode.value} value={mode.value}>
                      {mode.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field
              label="テンプレート"
              required={form.generationMode !== 'ai'}
              error={
                form.generationMode !== 'ai' && !form.templateId
                  ? messages.campaign.templateRequired
                  : undefined
              }
              hint={
                form.generationMode === 'template'
                  ? 'テンプレートに企業名などを差し込みます。AI は使いません。'
                  : form.generationMode === 'template_ai'
                    ? 'テンプレートは固定のまま、確認された事項と NeSC の案内だけを企業の事業内容に合わせて AI が書きます（設定 → 外部連携 で AI の鍵が要ります）'
                    : '指定すると、その構成と語調を踏まえて AI が書きます'
              }
            >
              {(props) => (
                <Select
                  {...props}
                  value={form.templateId}
                  onChange={(e) => setForm((p) => ({ ...p, templateId: e.target.value }))}
                >
                  <option value="">使用しない</option>
                  {templates?.data.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="文体" required hint={form.generationMode !== 'ai' ? 'テンプレート差し込みでは使いません' : undefined}>
              {(props) => (
                <Select
                  {...props}
                  value={form.tone}
                  onChange={(e) => setForm((p) => ({ ...p, tone: e.target.value as never }))}
                >
                  <option value="polite">丁寧</option>
                  <option value="concise">簡潔</option>
                </Select>
              )}
            </Field>
            <Field label={messages.campaign.approvalPolicy} required>
              {(props) => (
                <Select
                  {...props}
                  value={form.approvalPolicy}
                  onChange={(e) => setForm((p) => ({ ...p, approvalPolicy: e.target.value as never }))}
                >
                  {ApprovalPolicies.list.map((policy) => (
                    <option key={policy.value} value={policy.value}>
                      {policy.label}（{policy.description}）
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            {/* 承認なしのときは警告を表示する */}
            {form.approvalPolicy === 'none' && (
              <p className="flex items-start gap-2 rounded-control border-2 border-foreground bg-muted p-3 text-body font-semibold">
                <AlertTriangle aria-hidden className="mt-0.5 size-5 shrink-0" />
                {messages.campaign.approvalNoneWarning}
              </p>
            )}

            <div className="border-t border-border pt-4">
              <div className="flex flex-wrap gap-2">
                <Button
                  size="lg"
                  onClick={() => generate.mutate()}
                  disabled={
                    generate.isPending ||
                    !campaignId ||
                    (form.generationMode !== 'ai' && !form.templateId)
                  }
                  disabledReason={
                    form.generationMode !== 'ai' && !form.templateId
                      ? messages.campaign.templateRequired
                      : undefined
                  }
                >
                  {form.generationMode !== 'ai' ? '文面を作成する' : messages.message.bulkGenerate}
                </Button>
                {campaignId && (
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => navigate(`/campaigns/${campaignId}/preview`)}
                  >
                    {messages.campaign.preview}
                  </Button>
                )}
              </div>
              <p className="mt-2 text-note text-foreground-muted">
                作成した文面は
                {form.approvalPolicy === 'none' ? 'そのまま送信されます' : '承認後に送信されます'}。
                送信前プレビューで実際に送られる本文を全件確認できます。
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>{messages.campaign.stepSchedule}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field label={messages.campaign.scheduledAt} hint="未指定の場合は即時開始します">
              {(props) => (
                <Input
                  {...props}
                  type="datetime-local"
                  value={form.scheduledAt}
                  onChange={(e) => setForm((p) => ({ ...p, scheduledAt: e.target.value }))}
                  disabled={form.immediate}
                />
              )}
            </Field>
            <label className="flex items-start gap-2 text-body">
              <input
                type="checkbox"
                className="mt-1 size-4 shrink-0"
                checked={form.immediate}
                onChange={(e) =>
                  setForm((p) => ({ ...p, immediate: e.target.checked, scheduledAt: e.target.checked ? '' : p.scheduledAt }))
                }
              />
              <span className="min-w-0">
                <span className="block font-semibold">{messages.campaign.immediateOption}</span>
                <span className="block text-note text-foreground-muted">{messages.campaign.sendNowRateNote}</span>
              </span>
            </label>

            <fieldset className="space-y-2">
              <legend className="text-body font-semibold">{messages.campaign.allowedHours}</legend>
              <div className="flex flex-wrap gap-2">
                {['日', '月', '火', '水', '木', '金', '土'].map((day, index) => (
                  <label
                    key={day}
                    className={cn(
                      'flex size-touch cursor-pointer items-center justify-center rounded-control border text-body',
                      form.allowedHours.daysOfWeek.includes(index)
                        ? 'border-2 border-foreground bg-muted font-semibold'
                        : 'border-border text-foreground-muted',
                    )}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={form.allowedHours.daysOfWeek.includes(index)}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          allowedHours: {
                            ...p.allowedHours,
                            daysOfWeek: e.target.checked
                              ? [...p.allowedHours.daysOfWeek, index].sort()
                              : p.allowedHours.daysOfWeek.filter((d) => d !== index),
                          },
                        }))
                      }
                    />
                    {day}
                  </label>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  max={23}
                  value={form.allowedHours.startHour}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      allowedHours: { ...p.allowedHours, startHour: Number(e.target.value) },
                    }))
                  }
                  aria-label="開始時刻"
                  className="w-24"
                />
                <span aria-hidden>時 〜</span>
                <Input
                  type="number"
                  min={1}
                  max={24}
                  value={form.allowedHours.endHour}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      allowedHours: { ...p.allowedHours, endHour: Number(e.target.value) },
                    }))
                  }
                  aria-label="終了時刻"
                  className="w-24"
                />
                <span aria-hidden>時</span>
              </div>
            </fieldset>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={messages.campaign.dailyLimit} required>
                {(props) => (
                  <Input
                    {...props}
                    type="number"
                    min={1}
                    value={form.dailyLimit}
                    onChange={(e) => setForm((p) => ({ ...p, dailyLimit: Number(e.target.value) }))}
                  />
                )}
              </Field>
              <Field label={messages.campaign.totalLimit} hint="未指定の場合は対象全件">
                {(props) => (
                  <Input
                    {...props}
                    type="number"
                    min={1}
                    value={form.totalLimit}
                    onChange={(e) => setForm((p) => ({ ...p, totalLimit: e.target.value }))}
                  />
                )}
              </Field>
            </div>

            <Field label={`${messages.campaign.domainInterval}（時間）`} required>
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  value={form.domainIntervalHours}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, domainIntervalHours: Number(e.target.value) }))
                  }
                />
              )}
            </Field>

            <fieldset className="space-y-2">
              <legend className="text-body font-semibold">{messages.campaign.followup}</legend>
              <label className="flex min-h-touch items-center gap-2 text-body">
                <input
                  type="checkbox"
                  className="size-4 accent-foreground"
                  checked={form.followupEnabled}
                  onChange={(e) => setForm((p) => ({ ...p, followupEnabled: e.target.checked }))}
                />
                返信がない企業に追客する
              </label>
              {form.followupEnabled && (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={90}
                    value={form.followupDays}
                    onChange={(e) => setForm((p) => ({ ...p, followupDays: Number(e.target.value) }))}
                    aria-label="追客までの日数"
                    className="w-24"
                  />
                  <span className="text-body">日後に追客文を送る（返信があれば停止）</span>
                </div>
              )}
            </fieldset>
          </CardContent>
        </Card>
      )}

      <div className="mt-4 flex justify-between gap-2">
        <Button
          variant="secondary"
          size="lg"
          onClick={() => (step === 0 ? goBack() : goToStep(step - 1))}
        >
          {step === 0 ? messages.common.cancel : messages.common.back}
        </Button>
        <Button size="lg" onClick={() => void next()} disabled={saveDraft.isPending}>
          {step === STEPS.length - 1 ? messages.campaign.start : messages.common.next}
        </Button>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={messages.campaign.start}
        summary={messages.campaign.confirmStart(preflight?.data.sendable ?? 0)}
        detail={
          preflight && (
            <dl className="space-y-1 text-body">
              <Row label="対象">{formatNumber(preflight.data.sendable)} 社</Row>
              <Row label="除外">
                {formatNumber(preflight.data.blocked.reduce((sum, b) => sum + b.count, 0))} 社（
                {preflight.data.blocked.map((b) => `${b.label} ${b.count}`).join('・')}）
              </Row>
              <Row label="開始">
                {form.immediate
                  ? '即時（送信可能時間帯・1 日上限を無視）'
                  : form.scheduledAt
                    ? form.scheduledAt.replace('T', ' ')
                    : '即時（送信可能時間帯内・1 日上限まで）'}
              </Row>
              <Row label="1日上限">
                {formatNumber(form.dailyLimit)} 社（{messages.campaign.estimatedDays(preflight.data.estimatedDays)}）
              </Row>
              <Row label="想定費用">{formatUsd(preflight.data.estimatedCostUsd)}</Row>
              <p className="pt-2 text-foreground-muted">{messages.campaign.confirmStartNote}</p>
            </dl>
          )
        }
        confirmText={messages.campaign.start}
        onConfirm={() => start.mutate()}
        isPending={start.isPending}
      />
    </>
  );
};

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex gap-3">
    <dt className="w-24 shrink-0 text-foreground-muted">{label}</dt>
    <dd className="tabular min-w-0 flex-1">{children}</dd>
  </div>
);
